import { motion } from "motion/react";
import React, { ChangeEvent, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { CrossIcon, SearchIcon, TriangleArrowDownIcon, TriangleArrowUpIcon } from "@/shared/icons";

type Props = {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  show?: boolean;
  currSearchIndex: number;
  upSearchIndex: () => void;
  downSearchIndex: () => void;
  total: number;
  onClose: () => void;
  onKeyDown?: (e: SyntheticEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const animations = {
  show: {
    opacity: 1,
  },
  hide: {
    opacity: 0,
  },
};

function ChatSearch({
  value,
  onChange,
  show = true,
  onClose,
  upSearchIndex,
  downSearchIndex,
  currSearchIndex,
  total,
  onKeyDown,
  onFocus,
  onBlur,
}: Props) {
  const { t } = useTranslation("chat");

  return (
    <motion.div
      initial={{ opacity: 0 }}
      variants={animations}
      animate={show ? "show" : "hidden"}
      className={clsx(
        "relative z-1 h-[2.625rem] shrink-0 px-4 w-full flex items-center gap-2 rounded-[0.5rem]",
        "bg-gradient-to-r from-dark-900/80 to-dark-900/60",
      )}
    >
      <SearchIcon size={16} className="text-white/60 transition" />
      <input
        className="bg-transparent grow font-medium text-15 text-white/70"
        onInput={onChange}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        value={value}
        maxLength={10000}
        placeholder={t("chat.enterSearch")}
        spellCheck="false"
      />
      <div className="text-13 font-bold tabular-nums">
        {currSearchIndex}/{total}
      </div>
      <div className="w-0.5 h-3 bg-white/10 mx-1" />
      <button type="button" className="group" onClick={downSearchIndex}>
        <TriangleArrowUpIcon
          size={12}
          className="text-white/60 group-hover:text-white transition"
        />
      </button>
      <button type="button" className="group" onClick={upSearchIndex}>
        <TriangleArrowDownIcon
          size={12}
          className="text-white/60 group-hover:text-white transition"
        />
      </button>
      <button type="button" className="group" onClick={onClose}>
        <CrossIcon size={12} className="text-white/60 group-hover:text-white transition" />
      </button>
    </motion.div>
  );
}

export default React.memo(ChatSearch);
